import api from '@/shared/api/api';
import {
  Receivable,
  PaginatedReceivablesResponse,
  GetReceivablesParams,
  CreateReceivablePayload,
  UpdateReceivablePayload,
  ReceivableDocument,
  CreateReceivableDocumentPayload,
  UpdateReceivableDocumentPayload,
  ReceivableDocumentType,
  ReceivableTax,
  CreateReceivableTaxPayload,
  UpdateReceivableTaxPayload,
  ReceivableTaxDocument,
  CreateReceivableTaxDocumentPayload,
  PaginatedReceivableTaxesResponse,
  ReceivableTaxDocumentType,
} from '../utils/types';

const buildDocumentFormData = (payload: {
  receivableId?: string;
  documentType?: ReceivableDocumentType;
  description?: string;
  file?: File;
}) => {
  const form = new FormData();
  form.append('data', JSON.stringify({
    receivableId: payload.receivableId,
    documentType: payload.documentType,
    description: payload.description,
  }));
  form.append('file', payload.file);
  return form;
};

const buildTaxPayload = (payload: {
  receivableId?: string;
  currency?: string;
  type?: string;
  description?: string;
  document?: string;
  value?: number;
}) => {
  const body: Record<string, unknown> = {};
  if (payload.receivableId) body.receivableId = payload.receivableId;
  if (payload.currency) body.currency = payload.currency;
  if (payload.type) body.type = payload.type;
  if (payload.description !== undefined) body.description = payload.description;
  if (payload.document !== undefined) body.document = payload.document;
  if (payload.value !== undefined) body.value = payload.value;
  return body;
};

const buildTaxDocumentFormData = (payload: {
  receivableTaxId?: string;
  documentType?: ReceivableTaxDocumentType;
  description?: string;
  file?: File;
}) => {
  const form = new FormData();
  form.append('data', JSON.stringify({
    receivable_tax_id: payload.receivableTaxId,
    documentType: payload.documentType,
    description: payload.description,
  }));
  form.append('file', payload.file);
  return form;
};

export const receivableApi = {
  list: async (
    params?: GetReceivablesParams,
    signal?: AbortSignal
  ): Promise<PaginatedReceivablesResponse> => {
    // Mantém camelCase para os parâmetros
    const mapped: Record<string, unknown> = {};
    const p = params ?? {};
    if (p.page !== undefined) mapped.page = p.page;
    if (p.limit !== undefined) mapped.limit = p.limit;
    if (p.status && p.status !== 'all') mapped.status = p.status;
    if (p.store && p.store !== 'all') mapped.store = p.store;
    if (p.relatedDateStart) mapped.relatedDateStart = p.relatedDateStart;
    if (p.relatedDateEnd) mapped.relatedDateEnd = p.relatedDateEnd;
    if (p.paidDateStart) mapped.paidDateStart = p.paidDateStart;
    if (p.paidDateEnd) mapped.paidDateEnd = p.paidDateEnd;
    // if (p.shareConfigId) mapped.receivableShareConfigId = p.shareConfigId;
    // if (p.createdBy) mapped.createdBy = p.createdBy;
    if (p.search) mapped.search = p.search;
    return await api<PaginatedReceivablesResponse>({
      url: '/v1/receivables',
      method: 'GET',
      params: mapped,
      signal,
    });
  },

  get: async (id: string, signal?: AbortSignal): Promise<Receivable> => {
    return api<Receivable>({
      url: `/v1/receivables/${id}`,
      method: 'GET',
      signal,
    });
  },

  create: async (payload: CreateReceivablePayload): Promise<Receivable> => {
    const data: Record<string, unknown> = {
      store: payload.store,
      status: payload.status,
      relatedDate: payload.relatedDate,
      grossValue: payload.grossValue,
      currency: payload.currency,
    };
    if (payload.paidDate !== undefined) data.paidDate = payload.paidDate;


    return api<Receivable>({
      url: '/v1/receivables',
      method: 'POST',
      data:payload,
    });
  },

  update: async (id: string, data: UpdateReceivablePayload): Promise<Receivable> => {

    return api<Receivable>({
      url: `/v1/receivables/${id}`,
      method: 'PATCH',
      data,
    });
  },

  remove: async (id: string): Promise<{ id: string; deletedAt: string }> => {
    return api({
      url: `/v1/receivables/${id}`,
      method: 'DELETE',
    });
  },

  documents: {
    list: async (receivableId: string, signal?: AbortSignal): Promise<ReceivableDocument[]> => {
      return api<ReceivableDocument[]>({
        url: `/v1/receivables/${receivableId}/documents`,
        method: 'GET',
        signal,
      });
    },

    create: async (payload: CreateReceivableDocumentPayload): Promise<ReceivableDocument> => {
      const form = buildDocumentFormData(payload);
      return api<ReceivableDocument>({
        url: `/v1/receivables/${payload.receivableId}/documents`,
        method: 'POST',
        data: form,
      });
    },

    update: async (payload: UpdateReceivableDocumentPayload): Promise<ReceivableDocument> => {
      const { id, ...rest } = payload;
      const form = buildDocumentFormData(rest);
      return api<ReceivableDocument>({
        url: `/v1/receivables/${payload.receivableId}/documents/${id}`,
        method: 'POST',
        data: form,
      });
    },

    remove: async (receivableId: string, id: string): Promise<{ receivableId: string; id: string; deletedAt?: string }> => {
      return api({
        url: `/v1/receivables/${receivableId}/documents/${id}`,
        method: 'DELETE',
      });
    },
  },

  taxes: {
    list: async (receivableId: string, search?: string, signal?: AbortSignal): Promise<ReceivableTax[]> => {
      const { data } = await api<PaginatedReceivableTaxesResponse>({
        url: '/v1/receivables-taxes',
        method: 'GET',
        params: { receivableId, search },
        signal,
      });
      return data;
    },

    create: async (payload: CreateReceivableTaxPayload): Promise<ReceivableTax> => {
      const data = buildTaxPayload({
        receivableId: payload.receivableId,
        currency: payload.currency,
        type: payload.type,
        description: payload.description,
        document: payload.document,
        value: payload.value,
      });
      return api<ReceivableTax>({
        url: '/v1/receivables-taxes',
        method: 'POST',
        data,
      });
    },

    update: async (payload: UpdateReceivableTaxPayload): Promise<ReceivableTax> => {
      const { id, ...rest } = payload;
      const data = buildTaxPayload(rest);
      return api<ReceivableTax>({
        url: `/v1/receivables-taxes/${id}`,
        method: 'PUT',
        data,
      });
    },

    remove: async (id: string): Promise<{ id: string; deletedAt?: string }> => {
      return api({
        url: `/v1/receivables-taxes/${id}`,
        method: 'DELETE',
      });
    },

    documents: {
      list: async (taxId: string, signal?: AbortSignal): Promise<ReceivableTaxDocument[]> => {
        return api<ReceivableTaxDocument[]>({
          url: `/v1/receivables-taxes/${taxId}/documents`,
          method: 'GET',
          signal,
        });
      },

      create: async (payload: CreateReceivableTaxDocumentPayload): Promise<ReceivableTaxDocument> => {
        const form = buildTaxDocumentFormData(payload);

        return api<ReceivableTaxDocument>({
          url: `/v1/receivables-taxes/${payload.receivableTaxId}/documents`,
          method: 'POST',
          data: form,
        });
      },

      remove: async ({ id, receivableId }: { id: string; receivableId: string }): Promise<{ id: string; deletedAt?: string }> => {
        return api({
          url: `/v1/receivables-taxes/${receivableId}/documents/${id}`,
          method: 'DELETE',
        });
      },
    },
  },
};
